import {
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  query,
  setDoc,
  updateDoc,
  where,
} from 'firebase/firestore';
import { db } from '../config/firebase';
import type { Workspace } from '../types';

// ===== コレクション定数 =====
const WORKSPACES_COLLECTION = 'workspaces';
const MESSAGES_SUBCOLLECTION = 'messages';
const READ_STATUS_SUBCOLLECTION = 'readStatus';

// ===== エラーハンドリング =====
class WorkspaceServiceError extends Error {
  public code?: string;

  constructor(message: string, code?: string) {
    super(message);
    this.name = 'WorkspaceServiceError';
    this.code = code;
  }
}

// ===== ヘルパー関数 =====
const generateId = (): string => {
  return `ws-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
};

// ===== 案件 CRUD =====

export const createWorkspace = async (
  userId: string,
  workspaceData: {
    name: string;
    description?: string;
    groupIds: string[];
  },
): Promise<Workspace> => {
  try {
    const workspaceId = generateId();
    const now = new Date().toISOString();

    const workspace: Workspace = {
      id: workspaceId,
      name: workspaceData.name,
      description: workspaceData.description,
      status: 'active',
      groupIds: workspaceData.groupIds,
      createdBy: userId,
      createdAt: now,
      updatedAt: now,
    };

    const workspaceRef = doc(db, WORKSPACES_COLLECTION, workspaceId);
    await setDoc(workspaceRef, workspace);

    return workspace;
  } catch (error) {
    console.error('Error creating workspace:', error);
    throw new WorkspaceServiceError('案件の作成に失敗しました');
  }
};

export const getWorkspace = async (workspaceId: string): Promise<Workspace | null> => {
  try {
    const workspaceRef = doc(db, WORKSPACES_COLLECTION, workspaceId);
    const workspaceSnap = await getDoc(workspaceRef);

    if (!workspaceSnap.exists()) {
      return null;
    }

    return workspaceSnap.data() as Workspace;
  } catch (error) {
    console.error('Error getting workspace:', error);
    throw new WorkspaceServiceError('案件の取得に失敗しました');
  }
};

export const getWorkspacesByGroups = async (
  groupIds: string[],
  includeArchived: boolean = false,
): Promise<Workspace[]> => {
  try {
    if (groupIds.length === 0) {
      return [];
    }

    // Firestoreの制限: array-contains-any は最大10個まで
    // 10個を超える場合は分割してクエリ
    const chunks: string[][] = [];
    for (let i = 0; i < groupIds.length; i += 10) {
      chunks.push(groupIds.slice(i, i + 10));
    }

    const allWorkspaces: Workspace[] = [];

    for (const chunk of chunks) {
      const q = query(
        collection(db, WORKSPACES_COLLECTION),
        where('groupIds', 'array-contains-any', chunk),
      );

      const workspacesSnap = await getDocs(q);
      const workspaces = workspacesSnap.docs.map((doc) => doc.data() as Workspace);
      allWorkspaces.push(...workspaces);
    }

    // 重複を除去（複数のグループに所属している案件）
    const uniqueWorkspaces = Array.from(new Map(allWorkspaces.map((ws) => [ws.id, ws])).values());

    // ステータスでフィルタ
    const filteredWorkspaces = includeArchived
      ? uniqueWorkspaces
      : uniqueWorkspaces.filter((ws) => ws.status === 'active');

    // 更新日時でソート（新しい順）
    return filteredWorkspaces.sort(
      (a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime(),
    );
  } catch (error) {
    console.error('Error getting workspaces by groups:', error);
    throw new WorkspaceServiceError('案件一覧の取得に失敗しました');
  }
};

export const getWorkspacesByGroup = async (
  groupId: string,
  includeArchived: boolean = false,
): Promise<Workspace[]> => {
  try {
    const q = query(
      collection(db, WORKSPACES_COLLECTION),
      where('groupIds', 'array-contains', groupId),
    );

    const workspacesSnap = await getDocs(q);
    let workspaces = workspacesSnap.docs.map((doc) => doc.data() as Workspace);

    // ステータスでフィルタ
    if (!includeArchived) {
      workspaces = workspaces.filter((ws) => ws.status === 'active');
    }

    // 更新日時でソート（新しい順）
    return workspaces.sort(
      (a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime(),
    );
  } catch (error) {
    console.error('Error getting workspaces by group:', error);
    throw new WorkspaceServiceError('案件一覧の取得に失敗しました');
  }
};

export const updateWorkspace = async (
  workspaceId: string,
  updates: Partial<Pick<Workspace, 'name' | 'description' | 'status' | 'groupIds'>>,
): Promise<void> => {
  try {
    const workspaceRef = doc(db, WORKSPACES_COLLECTION, workspaceId);
    await updateDoc(workspaceRef, {
      ...updates,
      updatedAt: new Date().toISOString(),
    });
  } catch (error) {
    console.error('Error updating workspace:', error);
    throw new WorkspaceServiceError('案件の更新に失敗しました');
  }
};

export const deleteWorkspace = async (workspaceId: string): Promise<void> => {
  try {
    // チャットは廃止したが、過去に投稿されたものが残っているので一緒に消す
    const messagesRef = collection(db, WORKSPACES_COLLECTION, workspaceId, MESSAGES_SUBCOLLECTION);
    const messagesSnap = await getDocs(messagesRef);
    for (const messageDoc of messagesSnap.docs) {
      await deleteDoc(messageDoc.ref);
    }

    // readStatusサブコレクションを削除
    const readStatusRef = collection(
      db,
      WORKSPACES_COLLECTION,
      workspaceId,
      READ_STATUS_SUBCOLLECTION,
    );
    const readStatusSnap = await getDocs(readStatusRef);
    for (const statusDoc of readStatusSnap.docs) {
      await deleteDoc(statusDoc.ref);
    }

    // 案件ドキュメントを削除
    const workspaceRef = doc(db, WORKSPACES_COLLECTION, workspaceId);
    await deleteDoc(workspaceRef);
  } catch (error) {
    console.error('Error deleting workspace:', error);
    throw new WorkspaceServiceError('案件の削除に失敗しました');
  }
};

export const archiveWorkspace = async (workspaceId: string): Promise<void> => {
  try {
    await updateWorkspace(workspaceId, { status: 'archived' });
  } catch (error) {
    console.error('Error archiving workspace:', error);
    throw new WorkspaceServiceError('案件のアーカイブに失敗しました');
  }
};

export const unarchiveWorkspace = async (workspaceId: string): Promise<void> => {
  try {
    await updateWorkspace(workspaceId, { status: 'active' });
  } catch (error) {
    console.error('Error unarchiving workspace:', error);
    throw new WorkspaceServiceError('案件のアーカイブ解除に失敗しました');
  }
};

// ===== メッセージ機能 =====
